import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Check, Loader2, AlertCircle, User, Calendar, CreditCard,
  FileSignature, Plus, Trash2, ChevronRight, ChevronsUpDown, DollarSign, Shield, FileText
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { API } from "../config/api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function errToMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => {
      const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
      return field ? `${field}: ${d?.msg || "invalid"}` : (d?.msg || "invalid");
    }).join("; ");
  }
  return fallback;
}

const POLE_LENGTHS = ["9'0", "10'0", "10'6", "11'0", "11'6", "12'1", "12'7", "13'1", "13'7", "14'1", "14'7", "15'1", "15'7", "16'1", "16'5", "16'9"];
const POLE_WEIGHTS = ["80", "85", "90", "95", "100", "105", "110", "115", "120", "125", "130", "135", "140", "145", "150", "155", "160", "165", "170", "175", "180", "185", "190", "195", "200", "210", "220"];

function SearchableSelect({ value, onChange, options, placeholder = "Select…", testId, allowCustom = true }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const hasCustomMatch = allowCustom && search && !options.includes(search);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid={testId} type="button" variant="outline" role="combobox" className="w-full justify-between font-normal">
          <span className={value ? "" : "text-slate-400"}>{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput placeholder="Search or type…" value={search} onValueChange={setSearch} data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>
              {allowCustom && search ? (
                <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100" onClick={() => { onChange(search); setOpen(false); setSearch(""); }}>
                  Use <strong>&quot;{search}&quot;</strong>
                </button>
              ) : <span className="text-sm text-slate-400 px-3 py-2 block">No matches</span>}
            </CommandEmpty>
            <CommandGroup>
              {hasCustomMatch && (
                <CommandItem key={`custom-${search}`} value={`__custom__${search}`} onSelect={() => { onChange(search); setOpen(false); setSearch(""); }}>
                  Use <span className="ml-1 font-semibold">&quot;{search}&quot;</span>
                </CommandItem>
              )}
              {options.map((opt) => (
                <CommandItem key={opt} value={opt} onSelect={() => { onChange(opt); setOpen(false); setSearch(""); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === opt ? "opacity-100" : "opacity-0"}`} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const AGREEMENT_TEXT = `Circus Pole Vault LLC — Pole Vault Rental Agreement & Assumption of Risk

This Rental Agreement ("Agreement") is entered into between Circus Pole Vault LLC ("Lessor") and the undersigned renter ("Renter"), which may be an individual, school, club, or organization.

1. Rental Term & Auto-Renewal. All rentals are month-to-month unless otherwise stated on an invoice. Upon expiration of an invoice period, rentals automatically renew month-to-month at the standard rate until all equipment is returned and received by Circus Pole Vault LLC.

2. Responsibility for Use & Users. The Renter assumes full responsibility for the rented equipment and for ALL persons who use, access, or handle the equipment, including but not limited to athletes, students, coaches, and third parties.

3. Assumption of Risk & Release of Liability. Pole vaulting and related training activities involve inherent risks of serious injury or death. The Renter knowingly and voluntarily assumes all risks associated with the use of any rented equipment. Circus Pole Vault LLC shall NOT be liable for any injury, death, property damage, or loss arising from the use, misuse, or condition of the equipment, to the fullest extent permitted by law.

4. Equipment Responsibility. The Renter is financially responsible for any lost, stolen, destroyed, or damaged poles, regardless of cause.

5. Optional Insurance. If insurance is purchased, coverage is limited solely to damaged or broken poles occurring during normal use. Insurance does not cover loss, theft, misuse, negligence, or improper storage.

6. Pole Exchanges. Pole exchanges are subject to availability and incur a $35 exchange/restocking fee per pole.

7. ESSX Special Orders. Poles specially ordered from ESSX require a minimum two (2) month rental commitment.

8. Scope of Agreement. This Agreement applies to the equipment listed on any invoice and to any future rentals from Circus Pole Vault LLC.

9. Age, Authority & Minors. The undersigned certifies they are at least eighteen (18) years old and legally authorized to sign.

10. Governing Law. This Agreement shall be governed by the laws of the state in which Circus Pole Vault LLC operates.`;


// ---------- Signature Pad (shared shape with the meet form) ----------
function SignaturePad({ onSignatureChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const getPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: cx - r.left, y: cy - r.top };
  };
  const startDraw = (e) => { e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); setIsDrawing(true); };
  const draw = (e) => { if (!isDrawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.stroke(); };
  const endDraw = () => { if (!isDrawing) return; setIsDrawing(false); setHasSignature(true); onSignatureChange(canvasRef.current.toDataURL('image/png')); };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasSignature(false); onSignatureChange(''); };
  return (
    <div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white relative">
        <canvas ref={canvasRef} width={500} height={150} className="w-full cursor-crosshair touch-none" style={{ height: '150px' }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
        {!hasSignature && (<div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-sm">Sign here</div>)}
      </div>
      {hasSignature && (<Button variant="ghost" size="sm" onClick={clear} className="mt-1 text-xs text-slate-500">Clear signature</Button>)}
    </div>
  );
}


// ---------- Card form (Stripe Elements) ----------
function CardForm({ clientSecret, onPaymentMethodReady }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  useEffect(() => {
    if (stripe && elements && clientSecret && cardComplete) {
      onPaymentMethodReady(async () => {
        const cardEl = elements.getElement(CardElement);
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: cardEl } });
        if (error) throw new Error(error.message);
        return setupIntent.payment_method;
      });
    } else {
      onPaymentMethodReady(null);
    }
  }, [stripe, elements, clientSecret, cardComplete, onPaymentMethodReady]);
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <CardElement options={{ style: { base: { fontSize: '16px', color: '#1e293b', '::placeholder': { color: '#94a3b8' } }, invalid: { color: '#ef4444' } } }} onChange={(e) => setCardComplete(e.complete)} />
    </div>
  );
}


// ---------- Main ----------
function RentalRequestFormContent() {
  const today = new Date().toISOString().split('T')[0];
  const [step, setStep] = useState('form');

  const [form, setForm] = useState({
    athlete_name: '',
    athlete_is_minor: false,
    renter_name: '',
    phone: '',
    email: '',
    is_school: false,
    school_name: '',
    pay_with_invoice: false,
    invoice_billing: 'monthly', // 'upfront' or 'monthly'
    estimated_months: 3,
    pickup_date: '',
    return_date: '',
    authorized_rep: '',
    signature_date: today,
    agree_terms: false,
    notes: '',
  });
  const [poles, setPoles] = useState([{ length: '', weight: '' }]);
  const [includeInsurance, setIncludeInsurance] = useState(false);
  const [signature, setSignature] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [pricing, setPricing] = useState({}); // {length: monthly amount}

  const [stripeReady, setStripeReady] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeData, setStripeData] = useState({ clientSecret: '', customerId: '' });
  const [confirmPaymentFn, setConfirmPaymentFn] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  // Load pricing once
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/rental-requests/pricing`).then((res) => {
      if (!cancelled) setPricing(res.data.prices || {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // School + invoice options
  const isSchoolInvoice = form.is_school && form.pay_with_invoice;
  const skipCard = isSchoolInvoice;

  // Pole helpers
  const addPole = () => setPoles(p => [...p, { length: '', weight: '' }]);
  const removePole = (i) => setPoles(p => p.length > 1 ? p.filter((_, idx) => idx !== i) : p);
  const setPole = (i, field, val) => setPoles(p => p.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  // Renter info ready -> trigger SetupIntent (unless skipCard)
  const renterInfoReady = useMemo(() => {
    const emailLooksComplete = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email || '');
    return Boolean(
      form.athlete_name && form.phone && emailLooksComplete &&
      (!form.athlete_is_minor || form.renter_name)
    );
  }, [form.athlete_name, form.email, form.phone, form.athlete_is_minor, form.renter_name]);

  useEffect(() => {
    if (skipCard || !renterInfoReady) return;
    let cancelled = false;
    const fullName = form.athlete_is_minor && form.renter_name ? form.renter_name : form.athlete_name;
    const init = async () => {
      setStripeReady(false);
      setStripeLoading(true);
      try {
        const res = await axios.post(`${API}/rental-requests/setup-intent`, {
          full_name: fullName, email: form.email, phone: form.phone,
        });
        if (cancelled) return;
        setStripeData({ clientSecret: res.data.client_secret, customerId: res.data.customer_id });
        setStripeReady(true);
      } catch (err) {
        if (!cancelled) toast.error(errToMessage(err, "Failed to initialize payment"));
      } finally {
        if (!cancelled) setStripeLoading(false);
      }
    };
    const timer = setTimeout(init, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [renterInfoReady, skipCard, form.email, form.phone, form.athlete_name, form.renter_name, form.athlete_is_minor]);

  // Live monthly estimate
  const validPoles = poles.filter(p => p.length && p.weight);
  const monthlyEstimate = useMemo(() => {
    if (!Object.keys(pricing).length || validPoles.length === 0) return null;
    let poleTotal = 0;
    let allPriced = true;
    for (const p of validPoles) {
      const price = pricing[p.length];
      if (typeof price !== 'number') { allPriced = false; }
      poleTotal += price || 0;
    }
    const insuranceTotal = includeInsurance ? (pricing['Insurance'] || 10) * validPoles.length : 0;
    return { poleTotal, insuranceTotal, monthly: poleTotal + insuranceTotal, allPriced };
  }, [pricing, validPoles, includeInsurance]);

  const polesValid = poles.length > 0 && poles.every(p => p.length && p.weight);
  const datesValid = !!form.pickup_date && (!form.return_date || form.pickup_date <= form.return_date);
  const signerMatchesRenter = (
    !form.athlete_is_minor ||
    (form.authorized_rep || '').trim().toLowerCase() === (form.renter_name || '').trim().toLowerCase()
  );

  const canSubmit = (
    renterInfoReady && polesValid && datesValid &&
    form.agree_terms && form.authorized_rep && signature &&
    !honeypot && signerMatchesRenter &&
    (skipCard || (stripeReady && confirmPaymentFn))
  );

  const handleSubmit = async () => {
    if (honeypot) return;
    if (!canSubmit) {
      if (!datesValid) toast.error("Please enter a valid pickup date");
      else if (!polesValid) toast.error("Please complete every pole row (length + weight)");
      else if (!signerMatchesRenter) toast.error("Signer name must match the Renter (Coach/Parent/Guardian) for a minor athlete.");
      else toast.error("Please complete all required fields, sign, and add payment method");
      return;
    }
    setSubmitting(true);
    try {
      let paymentMethodId = null;
      let customerId = null;
      if (!skipCard) {
        try {
          paymentMethodId = await confirmPaymentFn();
          customerId = stripeData.customerId;
        } catch (err) {
          toast.error(`Card error: ${err.message}`);
          setSubmitting(false);
          return;
        }
      }
      await axios.post(`${API}/rental-requests/submit`, {
        athlete_name: form.athlete_name,
        athlete_is_minor: form.athlete_is_minor,
        renter_name: form.athlete_is_minor ? form.renter_name : '',
        phone: form.phone,
        email: form.email,
        is_school: form.is_school,
        school_name: form.is_school ? form.school_name : '',
        pay_with_invoice: isSchoolInvoice,
        invoice_billing: isSchoolInvoice ? form.invoice_billing : '',
        estimated_months: form.estimated_months || 1,
        pickup_date: form.pickup_date,
        return_date: form.return_date,
        poles: poles.map(p => ({ length: p.length, weight: p.weight })),
        include_insurance: includeInsurance,
        authorized_rep: form.authorized_rep,
        signature_date: form.signature_date,
        signature_data: signature,
        agree_terms: form.agree_terms,
        stripe_customer_id: customerId || '',
        stripe_payment_method_id: paymentMethodId || '',
        notes: form.notes,
      });
      toast.success("Rental request submitted!");
      setStep('complete');
    } catch (err) {
      toast.error(errToMessage(err, "Failed to submit"));
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="rental-request-complete-card">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Request received!</h2>
            <p className="text-slate-600 mb-2">
              Thanks {form.athlete_name}. Your rental request has been submitted to Circus Pole Vault.
            </p>
            {isSchoolInvoice ? (
              <p className="text-slate-600 mb-4">
                You chose to <strong>pay by invoice</strong>. We&apos;ll email an invoice to <strong>{form.email}</strong> once your rental is approved.
              </p>
            ) : (
              <p className="text-slate-600 mb-4">
                Your card is on file but <strong>has not been charged</strong>. We&apos;ll start your monthly subscription when you pick up the poles.
              </p>
            )}
            <p className="text-sm text-slate-500">You can close this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold uppercase tracking-wide mb-3">
            <FileText className="w-3.5 h-3.5" /> Rental Request
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Pole Vault Rental Request</h1>
          <p className="text-slate-500 text-sm mt-1">Looking to rent poles for the season? Fill this out and we&apos;ll get back to you.</p>
        </div>

        {/* New-form callout */}
        <div className="mb-6 rounded-lg border-2 border-red-300 bg-red-50 p-4" data-testid="req-new-form-callout">
          <p className="text-red-800 text-sm font-bold leading-snug">
            This form is brand new — if you run into any issues, please email us directly at{' '}
            <a href="mailto:James@circuspolevault.com" className="underline hover:text-red-900">
              James@circuspolevault.com
            </a>.
          </p>
        </div>

        {/* Renter Information */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><User className="w-4 h-4 text-slate-500" /> Renter Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Athlete&apos;s Name <span className="text-red-500">*</span></Label>
                <Input data-testid="req-athlete-name-input" value={form.athlete_name} onChange={(e) => setForm(f => ({ ...f, athlete_name: e.target.value }))} placeholder="Person using the poles" />
              </div>
              <div>
                <Label className="text-xs">Phone <span className="text-red-500">*</span></Label>
                <Input data-testid="req-phone-input" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(000) 000-0000" />
              </div>
              <div>
                <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                <Input data-testid="req-email-input" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" />
              </div>
              <div>
                <Label className="text-xs">School / Club (optional)</Label>
                <Input value={form.school_name} onChange={(e) => setForm(f => ({ ...f, school_name: e.target.value, is_school: !!e.target.value }))} placeholder="School or club name" />
              </div>
            </div>

            <div className="mt-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-2">
                <Checkbox data-testid="req-is-school-checkbox" id="req-is-school" checked={form.is_school} onCheckedChange={(v) => setForm(f => ({ ...f, is_school: !!v, pay_with_invoice: !!v && f.pay_with_invoice }))} />
                <Label htmlFor="req-is-school" className="text-sm cursor-pointer">I&apos;m renting on behalf of a school or club</Label>
              </div>
              {form.is_school && (
                <div className="mt-3 flex items-center gap-2">
                  <Checkbox data-testid="req-pay-invoice-checkbox" id="req-pay-invoice" checked={form.pay_with_invoice} onCheckedChange={(v) => setForm(f => ({ ...f, pay_with_invoice: !!v }))} />
                  <Label htmlFor="req-pay-invoice" className="text-sm cursor-pointer">Pay with invoice (school P.O. / billing)</Label>
                </div>
              )}
            </div>

            <div className="mt-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-2">
                <Checkbox data-testid="req-is-minor-checkbox" id="req-is-minor" checked={form.athlete_is_minor} onCheckedChange={(v) => setForm(f => ({ ...f, athlete_is_minor: !!v, renter_name: v ? f.renter_name : '' }))} />
                <Label htmlFor="req-is-minor" className="text-sm cursor-pointer">Athlete is a minor (under 18)</Label>
              </div>
              {form.athlete_is_minor && (
                <div className="mt-3">
                  <Label className="text-xs">Renter&apos;s Name — Coach / Parent / Guardian <span className="text-red-500">*</span></Label>
                  <Input data-testid="req-renter-name-input" value={form.renter_name} onChange={(e) => setForm(f => ({ ...f, renter_name: e.target.value }))} placeholder="Adult responsible for the minor athlete" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dates */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-500" /> Pickup &amp; Return Dates</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Pickup Date <span className="text-red-500">*</span></Label>
                <Input data-testid="req-pickup-date-input" type="date" value={form.pickup_date} onChange={(e) => setForm(f => ({ ...f, pickup_date: e.target.value }))} min={today} />
              </div>
              <div>
                <Label className="text-xs">Estimated Return Date <span className="text-slate-400">(optional)</span></Label>
                <Input data-testid="req-return-date-input" type="date" value={form.return_date} onChange={(e) => setForm(f => ({ ...f, return_date: e.target.value }))} min={form.pickup_date || today} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Poles */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm flex items-center gap-2"><FileSignature className="w-4 h-4 text-slate-500" /> Poles Needed <span className="text-red-500">*</span></h3>
              <span className="text-xs text-slate-500">All ESSX</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">Add one row per pole. Need two of the same? Just add it twice.</p>
            <div className="space-y-2">
              {poles.map((pole, i) => (
                <div key={i} className="flex gap-2 items-end" data-testid={`req-pole-row-${i}`}>
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Length</Label>
                    <SearchableSelect value={pole.length} onChange={(v) => setPole(i, 'length', v)} options={POLE_LENGTHS} placeholder="e.g. 13'7" testId={`req-pole-length-${i}`} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Weight</Label>
                    <SearchableSelect value={pole.weight} onChange={(v) => setPole(i, 'weight', v)} options={POLE_WEIGHTS} placeholder="e.g. 155" testId={`req-pole-weight-${i}`} />
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 mb-1" onClick={() => removePole(i)} disabled={poles.length === 1}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={addPole} data-testid="req-add-pole-btn">
              <Plus className="w-4 h-4" /> Add another pole
            </Button>
            <div className="mt-4 flex items-center gap-2">
              <Checkbox id="req-insurance" data-testid="req-insurance-checkbox" checked={includeInsurance} onCheckedChange={(v) => setIncludeInsurance(!!v)} />
              <Label htmlFor="req-insurance" className="text-sm cursor-pointer flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-slate-500" /> Add insurance (+${pricing['Insurance'] || 10}/pole/mo)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Estimate */}
        {monthlyEstimate && (
          <Card className="mb-6 border-emerald-200 bg-emerald-50/40" data-testid="req-estimate-card">
            <CardContent className="pt-4">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-600" /> Estimated Price</h3>
              {isSchoolInvoice && form.invoice_billing === 'upfront' ? (
                <>
                  <div className="text-3xl font-bold text-emerald-700" data-testid="req-estimate-total">
                    ${(monthlyEstimate.monthly * (form.estimated_months || 1)).toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {validPoles.length} pole{validPoles.length === 1 ? '' : 's'} × ${monthlyEstimate.monthly.toFixed(2)}/mo × {form.estimated_months || 1} month{(form.estimated_months || 1) === 1 ? '' : 's'} (upfront)
                  </div>
                </>
              ) : (
                <>
                  <div className="text-3xl font-bold text-emerald-700" data-testid="req-estimate-total">
                    ${monthlyEstimate.monthly.toFixed(2)}<span className="text-base font-normal text-slate-500">/mo</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {validPoles.length} pole{validPoles.length === 1 ? '' : 's'} • ${monthlyEstimate.poleTotal.toFixed(2)} poles
                    {includeInsurance && monthlyEstimate.insuranceTotal > 0 && ` + $${monthlyEstimate.insuranceTotal.toFixed(2)} insurance`}
                  </div>
                </>
              )}
              {!monthlyEstimate.allPriced && (
                <p className="text-[11px] text-amber-700 mt-2">Some pole sizes aren&apos;t in our standard price list — we&apos;ll confirm pricing before approving.</p>
              )}
              <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                <strong>Estimate only.</strong> {skipCard ? 'You\u2019ll receive an invoice once your rental is approved.' : 'Your card is saved but not charged until you pick up the poles.'}
              </div>
            </CardContent>
          </Card>
        )}

        {/* School + Invoice → upfront/monthly choice */}
        {isSchoolInvoice && (
          <Card className="mb-6 border-blue-200 bg-blue-50/40">
            <CardContent className="pt-4">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Invoice Billing</h3>
              <p className="text-xs text-slate-600 mb-3">Schools paying by invoice can choose to be billed upfront for the full term or month-to-month.</p>
              <RadioGroup value={form.invoice_billing} onValueChange={(v) => setForm(f => ({ ...f, invoice_billing: v }))}>
                <div className="flex items-start gap-3 p-3 bg-white rounded-md border border-slate-200">
                  <RadioGroupItem value="monthly" id="bill-monthly" data-testid="req-bill-monthly" />
                  <Label htmlFor="bill-monthly" className="text-sm cursor-pointer flex-1">
                    <strong>Monthly invoice</strong>
                    <div className="text-xs text-slate-500 mt-0.5">A new invoice is generated each month until the poles are returned.</div>
                  </Label>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-md border border-slate-200">
                  <RadioGroupItem value="upfront" id="bill-upfront" data-testid="req-bill-upfront" />
                  <Label htmlFor="bill-upfront" className="text-sm cursor-pointer flex-1">
                    <strong>Upfront invoice</strong>
                    <div className="text-xs text-slate-500 mt-0.5">Single invoice for the full rental term.</div>
                  </Label>
                </div>
              </RadioGroup>
              {form.invoice_billing === 'upfront' && (
                <div className="mt-3">
                  <Label className="text-xs">Number of months <span className="text-red-500">*</span></Label>
                  <Input type="number" min={1} max={12} value={form.estimated_months} onChange={(e) => setForm(f => ({ ...f, estimated_months: Math.max(1, parseInt(e.target.value) || 1) }))} className="w-32" data-testid="req-months-input" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Agreement */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><FileSignature className="w-4 h-4 text-slate-500" /> Rental Agreement</h3>
            <div className="bg-slate-50 rounded-lg p-4 max-h-[280px] overflow-y-auto text-xs text-slate-700 whitespace-pre-line leading-relaxed border">{AGREEMENT_TEXT}</div>
            <div className="flex items-center gap-2 mt-4">
              <Checkbox id="req-agree" data-testid="req-agree-checkbox" checked={form.agree_terms} onCheckedChange={(v) => setForm(f => ({ ...f, agree_terms: !!v }))} />
              <Label htmlFor="req-agree" className="text-sm cursor-pointer">I agree to the above terms <span className="text-red-500">*</span></Label>
            </div>
          </CardContent>
        </Card>

        {/* Signature */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><FileSignature className="w-4 h-4 text-slate-500" /> Signature <span className="text-red-500">*</span></h3>
            <div>
              <Label className="text-xs">Authorized Representative / Parent / Guardian <span className="text-red-500">*</span></Label>
              <Input data-testid="req-authorized-rep-input" value={form.authorized_rep} onChange={(e) => setForm(f => ({ ...f, authorized_rep: e.target.value }))}
                placeholder={form.athlete_is_minor && form.renter_name ? form.renter_name : "Printed name"}
                className={`mb-1 ${form.athlete_is_minor && form.authorized_rep && !signerMatchesRenter ? 'border-red-400' : ''}`} />
              {form.athlete_is_minor && (
                <p className={`text-[11px] mb-3 ${form.authorized_rep && !signerMatchesRenter ? 'text-red-600' : 'text-slate-500'}`}>
                  {form.authorized_rep && !signerMatchesRenter
                    ? `Must match the renter's name above ("${form.renter_name}"). Capitalization doesn't matter.`
                    : `Because the athlete is a minor, the signer must be the named renter ("${form.renter_name || '—'}").`}
                </p>
              )}
            </div>
            <SignaturePad onSignatureChange={setSignature} />
            <div className="mt-3">
              <Label className="text-xs">Signature Date</Label>
              <Input type="date" value={form.signature_date} onChange={(e) => setForm(f => ({ ...f, signature_date: e.target.value }))} className="w-48" />
            </div>
          </CardContent>
        </Card>

        {/* Payment OR Invoice notice */}
        {skipCard ? (
          <Card className="mb-6 bg-blue-50 border-blue-200" data-testid="req-invoice-notice-card">
            <CardContent className="pt-4">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Invoice Payment</h3>
              <p className="text-sm text-slate-700">No card needed — we&apos;ll send an invoice to <strong>{form.email || 'your email'}</strong> once your rental is approved.</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <h3 className="font-semibold text-sm mb-1 flex items-center gap-2"><CreditCard className="w-4 h-4 text-slate-500" /> Payment Method <span className="text-red-500">*</span></h3>
              <p className="text-sm font-bold text-slate-900 mb-3">We&apos;ll save your card now and only charge it when you pick up the poles.</p>
              {!renterInfoReady ? (
                <div className="flex items-center gap-2 py-4 text-slate-500 text-sm"><AlertCircle className="w-4 h-4" />Complete the renter information above to add a card.</div>
              ) : stripeLoading ? (
                <div className="flex items-center gap-2 py-4 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading payment form...</div>
              ) : stripeReady ? (
                <CardForm clientSecret={stripeData.clientSecret} onPaymentMethodReady={(fn) => setConfirmPaymentFn(() => fn)} />
              ) : (
                <div className="text-sm text-red-500">Could not load payment form. Please refresh.</div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Honeypot */}
        <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
          <input value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
        </div>

        <Button data-testid="req-submit-btn" size="lg" className="w-full" onClick={handleSubmit} disabled={!canSubmit || submitting}>
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><ChevronRight className="w-4 h-4 mr-2" /> Submit Rental Request</>}
        </Button>
        <p className="text-center text-xs text-slate-400 mt-3">
          {skipCard ? 'No charge — invoice payment.' : 'Secured by Stripe. Card encrypted and saved for pickup billing only.'}
        </p>
      </div>
    </div>
  );
}


export default function RentalRequestFormPage() {
  return (
    <Elements stripe={stripePromise}>
      <RentalRequestFormContent />
    </Elements>
  );
}
